/**
 * Shared field primitives for the public forms (spec sections 10, 14, 19).
 *
 * Everything in `src/shared` is imported by both the browser bundle and the
 * server, so this file may only depend on Zod. No server modules, no `node:`
 * imports, no environment access.
 *
 * The server is the authority: the browser may run these same schemas for
 * instant feedback, but every submission is re-validated here on the server
 * before anything is written (spec section 14).
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Honeypot
// ---------------------------------------------------------------------------

/**
 * Name of the hidden honeypot input carried by every public form (spec section
 * 19, CLAUDE.md "Honeypot from day one").
 *
 * `website` is the chosen name because it is the kind of field a naive form
 * bot expects to find and fill, while a human never sees it. The provider form
 * already has a *real* website field, and that one is `websiteUrl`, so the two
 * never collide in the payload.
 *
 * The A5 form work must render it so a human cannot trip it by accident:
 * visually hidden (not `display:none` alone, which some bots detect and skip),
 * `tabindex="-1"`, `autocomplete="off"`, and `aria-hidden="true"`.
 */
export const HONEYPOT_FIELD = 'website';

/**
 * The honeypot accepts *anything* — including values of the wrong type — and
 * never produces a validation error. A trap that answers "expected string,
 * received number" tells the bot exactly what it walked into.
 */
export const honeypotSchema = z.unknown().optional();

/** True when the hidden field came back with content, i.e. a bot filled it. */
export function isHoneypotTripped(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'boolean') return value;
  return true;
}

// ---------------------------------------------------------------------------
// String helpers
// ---------------------------------------------------------------------------

/** Trims, and treats a whitespace-only value the same as an absent one. */
export const optionalText = (max: number, message?: string) =>
  z.preprocess(
    (value) => {
      if (typeof value !== 'string') return value;
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    },
    z
      .string()
      .max(max, message ?? `Keep this under ${max} characters.`)
      .optional(),
  );

/** Trims, and requires a non-empty value within `min`/`max`. */
export const requiredText = (min: number, max: number, label: string) =>
  z.preprocess(
    (value) => (typeof value === 'string' ? value.trim() : value),
    z
      .string({ error: `${label} is required.` })
      .min(
        min,
        min === 1 ? `${label} is required.` : `${label} must be at least ${min} characters.`,
      )
      .max(max, `${label} must be ${max} characters or fewer.`),
  );

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

/** RFC 5321 limit; also the storage cap for `leads.email_normalized`. */
export const EMAIL_MAX_LENGTH = 254;

/**
 * Trimmed and lower-cased before validation, because the normalized form is
 * what uniqueness is checked against and the only form stored (spec section
 * 13, "Use a normalized lower case email for uniqueness checks").
 */
export const emailSchema = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim().toLowerCase() : value),
  z.email({ error: 'Enter a valid email address.' }).max(EMAIL_MAX_LENGTH, {
    error: 'That email address is too long.',
  }),
);

// ---------------------------------------------------------------------------
// ZIP code
// ---------------------------------------------------------------------------

/**
 * Exactly five digits, kept as text — leading zeros are real (spec section 13).
 *
 * Any valid US ZIP is accepted. Out-of-state signups are flagged, never
 * rejected: Maryland horse country crosses every border it has (spec section
 * 10, CLAUDE.md). A ZIP+4 suffix is dropped rather than refused.
 */
export const zipCodeSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    const plusFour = /^(\d{5})[- ]\d{4}$/.exec(trimmed);
    return plusFour?.[1] ?? trimmed;
  },
  z
    .string({ error: 'ZIP code is required.' })
    .regex(/^\d{5}$/, { error: 'Enter a 5 digit ZIP code.' }),
);

// ---------------------------------------------------------------------------
// Category slug
// ---------------------------------------------------------------------------

/**
 * Shape check only. Whether a slug names an *active* category is settled
 * against the database inside the signup transaction, not here — the taxonomy
 * is data, and a shared schema must not carry a copy of it that can drift.
 */
export const categorySlugSchema = z
  .string({ error: 'Choose a service category.' })
  .trim()
  .min(1, { error: 'Choose a service category.' })
  .max(64, { error: 'That category is not valid.' })
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, { error: 'That category is not valid.' });

// ---------------------------------------------------------------------------
// Phone
// ---------------------------------------------------------------------------

export const PHONE_MAX_LENGTH = 32;

/**
 * Normalizes to `+1XXXXXXXXXX` for US numbers, with ` ext. NNN` appended when
 * an extension was typed. Anything that is not recognizably a US number keeps
 * its digits (with a leading `+` if it had one) so an international or unusual
 * number is stored rather than mangled.
 *
 * Returns `null` when there is nothing usable to store.
 */
export function normalizePhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const extensionMatch = /(?:ext|extension|x)[.\s:]*(\d{1,6})\s*$/i.exec(trimmed);
  const extension = extensionMatch?.[1];
  const withoutExtension = extensionMatch ? trimmed.slice(0, extensionMatch.index) : trimmed;

  const digits = withoutExtension.replace(/\D/g, '');
  if (digits === '') return null;

  let base: string;
  if (digits.length === 10) {
    base = `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    base = `+${digits}`;
  } else {
    base = withoutExtension.trim().startsWith('+') ? `+${digits}` : digits;
  }

  const formatted = extension ? `${base} ext. ${extension}` : base;
  return formatted.slice(0, PHONE_MAX_LENGTH);
}

/** Counts the digits in a raw phone entry, used for the minimum-length check. */
function digitCount(value: string): number {
  return value.replace(/\D/g, '').length;
}

/**
 * Optional. A provider who types a genuinely odd number should not be blocked
 * from the founding list over it, so the only rule is "enough digits to be a
 * phone number at all" — seven, the length of a local US number.
 */
export const phoneSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') return value;
    return value.trim() === '' ? undefined : value;
  },
  z
    .string()
    .max(60, { error: 'That phone number is too long.' })
    .refine((value) => digitCount(value) >= 7, { error: 'Enter a valid phone number.' })
    .transform((value) => normalizePhone(value))
    .refine((value): value is string => value !== null, { error: 'Enter a valid phone number.' })
    .optional(),
);

// ---------------------------------------------------------------------------
// Website URL
// ---------------------------------------------------------------------------

export const URL_MAX_LENGTH = 300;

/**
 * Accepts `example.com` as readily as `https://example.com` — people type the
 * former — and normalizes to an absolute `https` URL. The scheme is restricted
 * to http/https so `javascript:` and friends can never reach a template, and
 * the host must contain a dot so a typo like "mysite" is caught here rather
 * than becoming a dead link in the admin list.
 */
export function normalizeWebsiteUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  // Any scheme at all is kept and then checked, rather than only `//` forms:
  // prepending `https://` to `mailto:sam@example.com` would otherwise produce
  // a valid URL with `sam` as credentials on host example.com.
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  // Credentials in a business web address are a mistake or an attack; either
  // way they are not something to store and later render.
  if (parsed.username !== '' || parsed.password !== '') return null;
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(parsed.hostname)) return null;
  if (parsed.href.length > URL_MAX_LENGTH) return null;

  return parsed.href;
}

export const websiteUrlSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') return value;
    return value.trim() === '' ? undefined : value;
  },
  z
    .string()
    .max(URL_MAX_LENGTH, { error: 'That web address is too long.' })
    .transform((value) => normalizeWebsiteUrl(value))
    .refine((value): value is string => value !== null, {
      error: 'Enter a valid web address, for example myfarm.com.',
    })
    .optional(),
);
